import { NotificationCategory, NotificationMethod } from '@tymbe/schema/enums';
import { NotificationData } from '@tymbe/schema/notification.interface';
import { Paginated } from '@feathersjs/feathers';
import { Form, FormState, FormApi } from 'informed';
import { useState, useRef } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import feathersClient from '../../../apiClient';
import { ErrorAlert, SuccessAlert } from '../../../components/alerts';
import { SubmitButton } from '../../../components/buttons';
import { PrimaryButton } from '@tymbe/ui-primitives';
import TyInput from '../../../components/inputs/TyInput';
import { TySelect } from '../../../components/inputs/TySelect';
import TyTextArea from '../../../components/inputs/TyTextArea';
import TyDateInput from '../../../components/inputs/TyDateInput';
import TyTimeInput from '../../../components/inputs/TyTimeInput';
import { PageTitle } from '../../../components/texts';
import Wrapper from '../../../components/wrapper';
import Container from '../../../containers';
import { TyTable, TyTableRow, TyTableCell, TyTablePagination, TyTableSortHeader, NoDataTableRow } from '@tymbe/ty-components/table';
import { ModalShell } from '@tymbe/ty-components/modals';
import useURLParamsHandler from '../../../hooks/UrlParamsHandler/useURLParamsHandler';
import defaultPageSizeOptions from '../../../components/Table/table.utils';
import { getPaginatedResponse } from '../../../apiClient/utils';

export interface PushNotificationFormValues {
  person_ids_raw: string;
  category: NotificationCategory;
  title: string;
  description: string;
  deep_link?: string;
  send_at_date?: Date;
  send_at_time?: Date;
  throttle: number;
}

const NOTIFICATION_CATEGORIES = [
  NotificationCategory.CUSTOM_NOTIFICATION,
  NotificationCategory.T24_MARKETING,
  NotificationCategory.T23_FREE_SHIFTS_AROUND_TYMBER,
];


const parsePersonIds = (rawInput: string): number[] => {
  // Split by comma, newline, or semicolon, filter empty strings, convert to numbers
  return rawInput
    .split(/[,\n;]/)
    .map(id => id.trim())
    .filter(id => id.length > 0)
    .map(id => parseInt(id, 10))
    .filter(id => !isNaN(id));
};

const PushNotificationsPage = () => {
  const [formDisabled, setFormDisabled] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [{ allSearchParams }, updateQueryParams] = useURLParamsHandler();
  const [searchParams] = useSearchParams();

  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) : 1;
  const pageSize = searchParams.get('pageSize') ? Number(searchParams.get('pageSize')) : defaultPageSizeOptions[0].value;
  const sort = allSearchParams.sort ?? { created_at: -1 };

  const { data: notificationsData, isLoading, refetch } = useQuery<Paginated<NotificationData>>(
    ['NotificationHistory', { currentPage, pageSize, sort, page: searchParams.get('page'), pageSizeParam: searchParams.get('pageSize') }],
    async () => {
      const result = await feathersClient.service('notification').find({
        query: {
          category: { $in: NOTIFICATION_CATEGORIES },
          $eager: '[createdBy]',
          $modify: ['whereNotNullCreatedBy'],
          $sort: sort,
          $skip: (currentPage - 1) * pageSize,
          $limit: pageSize,
        },
      });
      return getPaginatedResponse(result);
    },
    { keepPreviousData: true }
  );

  const { mutateAsync: createNotification } = useMutation(
    ['CreatePushNotification'],
    (req: any) => feathersClient.service('notification').create(req)
  );

  const validateUrl = (url: string | undefined): boolean => {
    if (!url || url.trim() === '') return true;

    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const onSubmit = async ({ values }: FormState<PushNotificationFormValues>) => {
    setFormDisabled(true);

    try {
      const person_ids = parsePersonIds(values.person_ids_raw);

      if (person_ids.length === 0) {
        ErrorAlert('Prosím zadejte alespoň jedno platné person_id');
        setFormDisabled(false);
        return;
      }

      // Validate deep link if provided
      if (values.deep_link && !validateUrl(values.deep_link)) {
        ErrorAlert('Deep link musí být platná URL adresa');
        setFormDisabled(false);
        return;
      }

      const category = typeof values.category === 'object' && values.category !== null
        ? (values.category as any).value
        : values.category;

      // Combine date and time - use provided values or fallback to current
      const dateMoment = values.send_at_date ? moment(values.send_at_date) : moment();
      const timeMoment = values.send_at_time ? moment(values.send_at_time) : moment();

      const send_at_combined = dateMoment
        .hour(timeMoment.hour())
        .minute(timeMoment.minute())
        .second(timeMoment.second())
        .toDate();

      const notificationData = {
        person_ids,
        category,
        method: [NotificationMethod.PUSH],
        payload: {
          push: {
            title: values.title,
            message: values.description,
            deep_link: values.deep_link?.trim() || null,
          },
          throttle_hours: values.throttle,
        },
        to_be_sent_at: send_at_combined.toISOString(),
      };

      await createNotification(notificationData);

      SuccessAlert(
        `Push notifikace byla úspěšně vytvořena pro ${person_ids.length} uživatele${person_ids.length > 1 ? 'ů' : ''}`
      );

      setShowCreateModal(false);
      updateQueryParams({ page: 1 }); // Go to first page to see the new notification
      await refetch();
    } catch (error) {
      console.error('Error creating push notification:', error);
      ErrorAlert('Při vytváření push notifikace nastala chyba');
    } finally {
      setFormDisabled(false);
    }
  };

  const getCategoryLabel = (category: NotificationCategory): string => {
    const option = categoryOptions.find(opt => opt.value === category);
    return option?.label || category;
  };

  const formatPayload = (notification: NotificationData): string => {
    if ('push' in notification.payload && notification.payload.push) {
      return notification.payload.push.title || '-';
    }
    return '-';
  };

  const categoryOptions = [
    { value: NotificationCategory.CUSTOM_NOTIFICATION, label: 'Custom Notification' },
    { value: NotificationCategory.T24_MARKETING, label: 'T24 - Marketing' },
    { value: NotificationCategory.T23_FREE_SHIFTS_AROUND_TYMBER, label: 'T23 - Volné směny v okolí' },
  ];

  const initialValues: PushNotificationFormValues = {
    person_ids_raw: '',
    category: NotificationCategory.CUSTOM_NOTIFICATION,
    title: '',
    description: '',
    deep_link: '',
    send_at_date: undefined,
    send_at_time: undefined,
    throttle: 12,
  };

  const headers = [
    { label: 'ID', value: 'id', sortable: true },
    { label: 'Nadpis', value: 'title', sortable: false },
    { label: 'Kategorie', value: 'category', sortable: true },
    { label: 'Počet příjemců', value: 'person_ids', sortable: false },
    { label: 'Vytvořil', value: 'created_by', sortable: false },
    { label: 'Metoda', value: 'method', sortable: false },
    { label: 'Vytvořeno', value: 'created_at', sortable: true },
    { label: 'Odeslat', value: 'to_be_sent_at', sortable: true },
    { label: 'Zpracováno', value: 'processed_at', sortable: true },
  ];

  return (
    <Container
      iconcolor="#B3CA1F"
      background="#fff"
      elevate
      contentstyle={{ paddingLeft: '170px' }}
      desktopHeader
      sidebar
    >
      <Wrapper padding="0px" margin="0px 22px 18px 31px">
        <div className="flex items-center justify-between mb-4">
          <PageTitle>Historie Push Notifikací</PageTitle>
          <PrimaryButton onClick={() => setShowCreateModal(true)}>
            Vytvořit notifikaci
          </PrimaryButton>
        </div>
        <Form>
          <TyTable>
            <thead>
              <TyTableRow>
                {headers.map((header) => (
                  <TyTableSortHeader
                    key={header.value}
                    field={header.sortable ? header.value : undefined}
                    onSort={header.sortable ? (field: string, sortOrder: number) => {
                      updateQueryParams({
                        sort: { [field]: sortOrder },
                        page: 1,
                      });
                    } : undefined}
                  >
                    {header.label}
                  </TyTableSortHeader>
                ))}
              </TyTableRow>
            </thead>
            <tbody>
              {isLoading && (
                <NoDataTableRow colSpan={headers.length}>
                  Načítám...
                </NoDataTableRow>
              )}
              {!isLoading && !notificationsData?.data?.length && (
                <NoDataTableRow colSpan={headers.length}>
                  Žádné záznamy nenalezeny
                </NoDataTableRow>
              )}
              {notificationsData?.data?.map((notification: NotificationData) => (
                <TyTableRow key={notification.id}>
                  <TyTableCell>{notification.id}</TyTableCell>
                  <TyTableCell>{formatPayload(notification)}</TyTableCell>
                  <TyTableCell>{getCategoryLabel(notification.category)}</TyTableCell>
                  <TyTableCell>{notification.person_ids?.length || 0}</TyTableCell>
                  <TyTableCell>
                    {notification.createdBy
                      ? `${notification.createdBy.first_name} ${notification.createdBy.last_name}`
                      : '-'}
                  </TyTableCell>
                  <TyTableCell>{notification.method?.join(', ').toUpperCase() || '-'}</TyTableCell>
                  <TyTableCell>{moment(notification.created_at).format('DD.MM.YYYY HH:mm')}</TyTableCell>
                  <TyTableCell>{moment(notification.to_be_sent_at).format('DD.MM.YYYY HH:mm')}</TyTableCell>
                  <TyTableCell>
                    {notification.processed_at ? moment(notification.processed_at).format('DD.MM.YYYY HH:mm') : 'Nezpracováno'}
                  </TyTableCell>
                </TyTableRow>
              ))}
            </tbody>
          </TyTable>

          <TyTablePagination
            rowsCount={notificationsData?.total || 0}
            pageSizeOptions={defaultPageSizeOptions}
          />
        </Form>
      </Wrapper>

      {/* Create Notification Modal */}
      <ModalShell
        showModal={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        className="w-[600px] max-w-[90vw] overflow-y-auto p-6"
      >
        <div className="w-full">
          <h2 className="text-2xl font-bold mb-4">Odeslat Push Notifikaci</h2>
          <Form<PushNotificationFormValues>
            name="push-notification-form"
            onSubmit={onSubmit}
            initialValues={initialValues}
          >
            <div className="flex flex-col gap-4">
              <TyTextArea
                name="person_ids_raw"
                label="Person IDs (oddělené čárkou nebo novým řádkem)"
                required
                rows={4}
                className="resize-none overflow-y-auto max-h-32"
              />

              <TySelect
                name="category"
                label="Kategorie notifikace"
                required
                options={categoryOptions}
              />

              <TyInput
                name="title"
                label="Nadpis"
                required
                maxLength={254}
              />

              <TyTextArea
                name="description"
                label="Popis"
                required
                rows={4}
                maxLength={254}
              />

              <TyInput
                name="deep_link"
                label="Deep Link (volitelné)"
              />

              <div className="grid grid-cols-2 gap-4">
                <TyDateInput
                  name="send_at_date"
                  label="Datum odeslání (volitelné, výchozí: nyní)"
                  className="w-full"
                  picker="date"
                />

                <TyTimeInput
                  name="send_at_time"
                  label="Čas odeslání (volitelné)"
                  className="w-full"
                />
              </div>

              <TyInput
                name="throttle"
                label="Throttle (hodiny)"
                type="number"
                required
              />

              <div className="text-sm text-gray-600">
                <p><strong>Poznámka:</strong> Throttle zajišťuje, že uživatel nedostane stejnou kategorii notifikace častěji než jednou za zadaný počet hodin.</p>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <SubmitButton
                disabled={formDisabled}
                buttontext="Odeslat Push Notifikaci"
              />
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              >
                Zrušit
              </button>
            </div>
          </Form>
        </div>
      </ModalShell>
    </Container>
  );
};

export default PushNotificationsPage;
